import { Box, VStack } from "@chakra-ui/react";
import React, { useState } from "react";

import { ConfirmModal } from "../../../../components";
import { ErrorContent } from "./ErrorContent";
import { ProcessingContent } from "./ProcessingContent";
import { UploadedContent } from "./UploadedContent";
import { UploadingContent } from "./UploadingContent";
import { VideoPreviewModal } from "./VideoPreviewModal";

type MediaFileCardStatus = "uploading" | "processing" | "uploaded" | "error";

type MediaFileCardProps = {
  status: MediaFileCardStatus;
  fileName: string;
  fileType: string;
  fileSizeBytes?: number;
  // Uploading-specific props
  progress?: number;
  uploadSpeed?: number;
  // Uploaded-specific props
  mediaUrl?: string;
  metaText?: string;
  // Error-specific props
  canRetry?: boolean;
  // Actions
  onPlay?: () => void;
  onRemove?: () => void;
  onRetry?: () => void;
  onCancel?: () => void;
  readOnly?: boolean;
  // Loading states
  isRemoving?: boolean;
};

export const MediaFileCard: React.FC<MediaFileCardProps> = ({
  status,
  fileName,
  fileType,
  fileSizeBytes,
  progress = 0,
  uploadSpeed,
  mediaUrl,
  metaText,
  canRetry,
  onPlay,
  onRemove,
  onRetry,
  onCancel,
  readOnly,
  isRemoving,
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const handlePlay = () => {
    if (onPlay) {
      onPlay();
    } else {
      setShowPreviewModal(true);
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      setShowRemoveConfirm(true);
    }
  };

  const cardPadding =
    status === "uploading" || status === "processing" || status === "uploaded"
      ? { pl: 8, pr: 4, py: 2 }
      : { pl: "32px", pr: status === "error" ? "8px" : "16px", py: "8px" };

  return (
    <VStack spacing={2} align="stretch">
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius={
          status === "uploading" || status === "processing" ? "md" : "6px"
        }
        {...cardPadding}
        bg="white"
        minH="120px"
        display="flex"
        alignItems="center"
      >
        {status === "uploading" && (
          <UploadingContent
            fileName={fileName}
            fileType={fileType}
            fileSizeBytes={fileSizeBytes}
            progress={progress}
            uploadSpeed={uploadSpeed}
            readOnly={readOnly}
            onCancel={onCancel}
          />
        )}
        {status === "processing" && (
          <ProcessingContent
            fileName={fileName}
            fileType={fileType}
            fileSizeBytes={fileSizeBytes}
            readOnly={readOnly}
          />
        )}
        {status === "uploaded" && (
          <UploadedContent
            fileName={fileName}
            fileType={fileType}
            metaText={metaText}
            mediaUrl={mediaUrl}
            readOnly={readOnly}
            isRemoving={isRemoving}
            onPlay={handlePlay}
            onRemove={handleRemove}
          />
        )}
        {status === "error" && (
          <ErrorContent
            fileName={fileName}
            fileType={fileType}
            metaText={metaText}
            canRetry={canRetry}
            readOnly={readOnly}
            onRetry={onRetry}
            onRemove={handleRemove}
          />
        )}
      </Box>
      {onRemove && (
        <ConfirmModal
          isOpen={showRemoveConfirm}
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={() => {
            onRemove();
            setShowRemoveConfirm(false);
          }}
          modalTitleText="Are you sure you want to delete this video?"
          modalBodyText="This will permanently delete the intro video. This cannot be undone."
          modalSubmitText="Delete"
          isDangerous
        />
      )}
      {status === "uploaded" && mediaUrl && (
        <VideoPreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          mediaUrl={mediaUrl}
          format={fileType}
        />
      )}
    </VStack>
  );
};
